import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Filter,
  RefreshCw,
  ArrowRight,
  CheckCircle2,
  Circle,
  Lock,
  FileText,
} from "lucide-react";
import api, { getUser } from "@/lib/api";

/**
 * Página `/care-plans` — dashboard global dos Planos de Cuidados Caring.
 *
 * Lê `GET /api/care-plans` (com filtros server-side por status/manager/q).
 * Cada linha mostra: cliente · beneficiários · gestor · versão actual ·
 * estado (draft/pending_approvals/frozen) · aprovação Direção Técnica.
 */
const STATUS_TABS = [
  { key: "all", label: "Todos" },
  { key: "draft", label: "Rascunho" },
  { key: "pending_approvals", label: "Em aprovação" },
  { key: "frozen", label: "Definitivo" },
];

function StatusBadge({ status }) {
  const map = {
    draft: {
      cls: "bg-muted text-muted-foreground border-border",
      label: "Rascunho",
    },
    pending_approvals: {
      cls: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
      label: "Em aprovação",
    },
    frozen: {
      cls: "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25",
      label: "Definitivo",
    },
  };
  const it = map[status] || map.draft;
  return (
    <span
      data-testid={`plan-status-badge-${status}`}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border ${it.cls}`}
    >
      {status === "frozen" ? <Lock className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
      {it.label}
    </span>
  );
}

function ApprovalDots({ approvals }) {
  // 25/02/2026 — reduzido de 3 para 2 aprovadores: DT + Gestor apenas.
  const items = [
    { k: "dt", label: "DT" },
    { k: "gestor", label: "Gestor" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="approval-dots">
      {items.map((it) => {
        const entry = approvals[it.k] || {};
        const approved = !!entry.approved;
        const byName = entry.by_name;
        return (
          <span
            key={it.k}
            title={
              approved
                ? `${it.label} aprovado${byName ? ` por ${byName}` : ""}`
                : `${it.label}: pendente`
            }
            data-testid={`approval-pill-${it.k}`}
            className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-1.5 py-0.5 border ${
              approved
                ? "bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border-[hsl(var(--success))]/25"
                : "bg-card text-muted-foreground border-[#E5E7EB]"
            }`}
          >
            {approved ? (
              <CheckCircle2 className="w-2.5 h-2.5" />
            ) : (
              <Circle className="w-2.5 h-2.5" />
            )}
            {it.label}
            {approved && byName && (
              <span className="opacity-70 font-normal">
                · {byName.split(" ")[0]}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

export default function CarePlans() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [managers, setManagers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const user = getUser();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (managerId) params.set("manager_id", managerId);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/care-plans?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar planos");
    } finally {
      setLoading(false);
    }
  };

  // Load managers para o filtro (admin/direcao vêm todos)
  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/users?role=gestor")
      .then((r) => setManagers(r.data || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, managerId]);

  const totals = useMemo(() => {
    const t = { all: items.length, draft: 0, pending_approvals: 0, frozen: 0 };
    for (const it of items) t[it.plan_status] = (t[it.plan_status] || 0) + 1;
    return t;
  }, [items]);

  return (
    <div className="max-w-[1500px] mx-auto p-6 lg:p-10" data-testid="care-plans-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
            Módulo Plano de Cuidados
          </div>
          <h1 className="font-ui text-4xl lg:text-5xl text-foreground font-bold leading-tight">
            Planos de Cuidados
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Todos os planos ativos com versão actual e estado das aprovações.
            Clica num lead para editar blocos ou descarregar `.docx` para
            Equipa Interna, Ajudante Familiar ou Cliente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={async () => {
                // Dry-run primeiro para mostrar o impacto ao admin.
                const r = await api.post("/care-plans/migrate-legacy", { dry_run: true });
                const willCreate = r.data?.created ?? 0;
                if (!willCreate) {
                  toast.success("Migração já está em dia — nenhum lead pendente");
                  return;
                }
                if (
                  !window.confirm(
                    `Vão ser criados ${willCreate} novos Master(s) V1 a partir de leads legacy.\n\nContinuar?`,
                  )
                ) return;
                const real = await api.post("/care-plans/migrate-legacy", { dry_run: false });
                toast.success(
                  `Migração concluída: ${real.data.created} criados · ${real.data.skipped_existing} ignorados${
                    real.data.errors?.length ? ` · ${real.data.errors.length} erros` : ""
                  }`,
                );
                load();
              }}
              data-testid="care-plans-migrate-btn"
              title="Migrar leads legacy que ainda não têm Master (idempotente — pode correr N vezes)"
              className="inline-flex items-center gap-2 text-xs bg-card hover:bg-muted border border-border text-muted-foreground rounded-full px-3 py-2"
            >
              Migrar legacy
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="care-plans-refresh-btn"
            className="inline-flex items-center gap-2 text-sm bg-card hover:bg-muted border border-border text-muted-foreground rounded-full px-4 py-2 disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Recarregar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-2xl p-5 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Procurar por nome do cliente..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="care-plans-search"
            className="w-full bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
        </div>
        {isAdmin && (
          <select
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            data-testid="care-plans-manager-filter"
            className="text-xs bg-card border border-border rounded-full px-3 py-2"
          >
            <option value="">Todos os gestores</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-1 flex-wrap" data-testid="care-plans-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                status === t.key
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-muted-foreground border-border hover:bg-muted"
              }`}
            >
              {t.label}
              {totals[t.key] > 0 && (
                <span className="ml-1.5 opacity-80">({totals[t.key] || 0})</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="care-plans-table">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-4 font-semibold">Cliente</th>
              <th className="text-left p-4 font-semibold">Beneficiário(s)</th>
              <th className="text-left p-4 font-semibold">Gestor</th>
              <th className="text-left p-4 font-semibold">Versão</th>
              <th className="text-left p-4 font-semibold">Estado</th>
              <th className="text-left p-4 font-semibold">Aprovações</th>
              <th className="text-left p-4 font-semibold">Actualizado</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground">
                  A carregar…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground" data-testid="care-plans-empty">
                  Nenhum plano encontrado.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr
                  key={it.master_id}
                  data-testid={`care-plan-row-${it.lead_id}`}
                  className="border-t border-[#F0EBDD] hover:bg-muted/40"
                >
                  <td className="p-4">
                    <div className="font-semibold text-foreground">
                      {it.contact_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                      {it.contact_email || "—"}
                    </div>
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">
                    {it.beneficiary_names.length > 0
                      ? it.beneficiary_names.join(" · ")
                      : "—"}
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">
                    {it.manager_name || <span className="text-muted-foreground">Sem gestor</span>}
                  </td>
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-[hsl(var(--info-bg))] rounded-full px-2.5 py-0.5">
                      <FileText className="w-3 h-3" />
                      {it.current_version_label || "—"}
                    </span>
                    {it.versions_count > 1 && (
                      <span
                        className="ml-2 text-[10px] text-muted-foreground"
                        title={`${it.versions_count} versões no histórico`}
                      >
                        · {it.versions_count} versões
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <StatusBadge status={it.plan_status} />
                  </td>
                  <td className="p-4">
                    <ApprovalDots approvals={it.approvals} />
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">
                    {it.updated_at
                      ? new Date(it.updated_at).toLocaleString("pt-PT", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="p-4">
                    <Link
                      to={`/leads/${it.lead_id}`}
                      data-testid={`care-plan-open-${it.lead_id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary"
                    >
                      Abrir <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-muted-foreground" data-testid="care-plans-legend">
        <Filter className="w-3 h-3 inline mr-1" />
        Total: <b>{items.length}</b> plano(s). Cada edição de bloco gera uma nova
        versão automática (V1 → V2 → V3…). Uma versão fica <b>Definitiva</b>{" "}
        quando DT + Gestor aprovam.
      </div>
    </div>
  );
}
